import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { DayPicker } from 'react-day-picker';
import {
  addMonths,
  addWeeks,
  endOfWeek,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import 'react-day-picker/dist/style.css';
import './week-picker.custom.css';

const pad2 = (value) => String(value).padStart(2, '0');

const formatLocalISO = (date) => {
  const year = date.getFullYear();
  const month = pad2(date.getMonth() + 1);
  const day = pad2(date.getDate());
  return `${year}-${month}-${day}`;
};

const parseISODate = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map((part) => Number(part));
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const parseMonthValue = (value) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return null;
  const [year, month] = value.split('-').map((part) => Number(part));
  const date = new Date(year, month - 1, 1);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};


const formatRangeLabel = (from, to) => {
  if (!from || !to) return '';
  const fromLabel = from.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  const toLabel = to.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  return `${fromLabel} \u2013 ${toLabel}`;
};

let weekPickerInstanceCounter = 0;

const WeekPicker = ({
  startInput,
  endInput,
  initialDate,
  startMonth,
  endMonth,
  disabledWeeksAhead,
  summaryFooterEl,
}) => {
  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    weekPickerInstanceCounter += 1;
    instanceIdRef.current = `wp_${weekPickerInstanceCounter}`;
  }
  const [selectedWeek, setSelectedWeek] = useState(() => {
    if (!initialDate) return undefined;
    return {
      from: startOfWeek(initialDate, { weekStartsOn: 1 }),
      to: endOfWeek(initialDate, { weekStartsOn: 1 }),
    };
  });

  useEffect(() => {
    if (!selectedWeek) {
      startInput.value = '';
      endInput.value = '';
    } else {
      if (selectedWeek.from) startInput.value = formatLocalISO(selectedWeek.from);
      if (selectedWeek.to) endInput.value = formatLocalISO(selectedWeek.to);
    }
  }, [selectedWeek, startInput, endInput]);

  const applySelection = (day, modifiers) => {
    if (modifiers.disabled || modifiers.hidden) return;
    if (modifiers.selected) {
      setSelectedWeek(undefined);
      return;
    }
    setSelectedWeek({
      from: startOfWeek(day, { weekStartsOn: 1 }),
      to: endOfWeek(day, { weekStartsOn: 1 }),
    });
  };

  const disabledMatcher = (() => {
    const today = startOfDay(new Date());
    const disableUntil =
      disabledWeeksAhead && disabledWeeksAhead > 0
        ? endOfWeek(addWeeks(today, disabledWeeksAhead), { weekStartsOn: 1 })
        : null;
    return (date) => {
      const day = startOfDay(date);
      if (day < today) return true;
      if (!disableUntil) return false;
      return day >= today && day <= disableUntil;
    };
  })();

  const showOutsideDays =
    !!selectedWeek &&
    !!selectedWeek.from &&
    !!selectedWeek.to &&
    selectedWeek.from.getMonth() !== selectedWeek.to.getMonth();

  const footerText =
    selectedWeek && selectedWeek.from && selectedWeek.to
      ? formatRangeLabel(selectedWeek.from, selectedWeek.to)
      : '';

  const footerNode = footerText ? (
    <span className="week-picker__date-range" key={footerText}>
      <span className="week-picker__date-range-text">{footerText}</span>
    </span>
  ) : null;

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('week-picker:summary-update'));
  }, [footerText]);

  return (
    <>
      <DayPicker
        showOutsideDays={showOutsideDays}
        weekStartsOn={1}
        fromMonth={startMonth || undefined}
        toMonth={endMonth || undefined}
        animate
        hideHead
        classNames={{
          day_outside: 'week-picker__day--outside',
        }}
        disabled={disabledMatcher}
        modifiers={{
          selected: selectedWeek,
          range_start: selectedWeek?.from,
          range_end: selectedWeek?.to,
          range_middle: (date) =>
            selectedWeek && selectedWeek.from && selectedWeek.to
              ? isWithinInterval(startOfDay(date), {
                  start: startOfDay(selectedWeek.from),
                  end: startOfDay(selectedWeek.to),
                })
              : false,
        }}
        onDayClick={applySelection}
        onDayKeyDown={(day, modifiers, e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            applySelection(day, modifiers);
          }
        }}
      />
      {summaryFooterEl && footerNode ? createPortal(footerNode, summaryFooterEl) : footerNode}
    </>
  );
};

const mountWeekPicker = (container) => {
  const rootEl = container.querySelector('[data-week-picker-root]');
  const startInput = container.querySelector('[data-week-start-input]');
  const endInput = container.querySelector('[data-week-end-input]');

  if (!rootEl || !startInput || !endInput) return;

  const monthsRangeRaw = container.dataset.monthsRange;
  const monthsRange = monthsRangeRaw ? Number(monthsRangeRaw) : null;
  const disabledWeeksRaw = container.dataset.disabledWeeks;
  const disabledWeeksAhead = disabledWeeksRaw ? Number(disabledWeeksRaw) : 0;
  const summaryFooterEl = container
    .closest('details')
    ?.querySelector('[data-week-picker-summary-footer]');
  let startMonth = parseMonthValue(container.dataset.startMonth);
  let endMonth = parseMonthValue(container.dataset.endMonth);

  if (Number.isFinite(monthsRange) && monthsRange > 0) {
    const now = new Date();
    const today = startOfDay(now);
    const safeDisabledWeeksAhead = Number.isFinite(disabledWeeksAhead) ? disabledWeeksAhead : 0;
    const disabledEnd =
      safeDisabledWeeksAhead > 0
        ? endOfWeek(addWeeks(today, safeDisabledWeeksAhead), { weekStartsOn: 1 })
        : today;
    startMonth = startOfMonth(disabledEnd);
    endMonth = addMonths(startMonth, monthsRange);
  }
  const initialDate =
    parseISODate(startInput.value) ||
    parseISODate(endInput.value) ||
    undefined;

  const root = createRoot(rootEl);
  root.render(
    <WeekPicker
      startInput={startInput}
      endInput={endInput}
      initialDate={initialDate}
      startMonth={startMonth}
      endMonth={endMonth}
      disabledWeeksAhead={disabledWeeksAhead}
      summaryFooterEl={summaryFooterEl || null}
    />
  );
};

const init = () => {
  document.querySelectorAll('[data-week-picker]').forEach((container) => {
    if (container.dataset.weekPickerMounted === 'true') return;
    container.dataset.weekPickerMounted = 'true';
    mountWeekPicker(container);
  });

  if (!window.__weekPickerSelectedDeliveryInit) {
    window.__weekPickerSelectedDeliveryInit = true;
    const updateSelectedDelivery = () => {
      const target = document.querySelector('[data-selected-delivery]');
      const summary = document.querySelector('.week-picker-summary');
      if (!target || !summary) return;
      const defaultText = target.dataset.defaultText || '';
      const dateRangeEl = summary.querySelector('[data-week-picker-summary-footer]');
      const hasSelection = !!dateRangeEl && dateRangeEl.textContent.trim() !== '';
      const summaryText = summary.textContent.trim();
      target.innerHTML = hasSelection ? summary.innerHTML : defaultText;
    };

    window.addEventListener('week-picker:summary-update', updateSelectedDelivery);
    updateSelectedDelivery();
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
